/** Ortalama Dünya yarıçapı (metre). */
export const EARTH_RADIUS_M = 6371008.8;

const toRad = (deg: number) => (deg * Math.PI) / 180;
const toDeg = (rad: number) => (rad * 180) / Math.PI;

/** Açıyı [0, 360) aralığına indirger. */
export function normalizeDeg(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/** İki açı arasındaki en kısa farkı (-180, 180] aralığında verir. */
export function angleDelta(from: number, to: number): number {
  let d = normalizeDeg(to - from);
  if (d > 180) d -= 360;
  return d;
}

/**
 * Başlangıç kerteriz açısı (initial great-circle bearing), kuzeyden
 * saat yönünde derece.
 */
export function initialBearing(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const p1 = toRad(lat1);
  const p2 = toRad(lat2);
  const dl = toRad(lon2 - lon1);
  const y = Math.sin(dl) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
  return normalizeDeg(toDeg(Math.atan2(y, x)));
}

/** İki nokta arasındaki büyük daire mesafesi (metre). */
export function distanceMeters(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const p1 = toRad(lat1);
  const p2 = toRad(lat2);
  const dp = toRad(lat2 - lat1);
  const dl = toRad(lon2 - lon1);
  const a =
    Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
}

const CARDINALS = [
  "Kuzey",
  "Kuzeydoğu",
  "Doğu",
  "Güneydoğu",
  "Güney",
  "Güneybatı",
  "Batı",
  "Kuzeybatı",
] as const;

/** Kerterizi Türkçe yön adına çevirir. */
export function cardinal(bearing: number): string {
  const i = Math.round(normalizeDeg(bearing) / 45) % 8;
  return CARDINALS[i];
}

/** Mesafeyi okunur biçime çevirir. */
export function formatDistance(meters: number): string {
  if (!Number.isFinite(meters)) return "—";
  if (meters < 1000) return `${Math.round(meters)} m`;
  if (meters < 100_000) return `${(meters / 1000).toFixed(1).replace(".", ",")} km`;
  return `${Math.round(meters / 1000).toLocaleString("tr-TR")} km`;
}

/** Kerterizi "134°" biçiminde yazar. */
export function formatBearing(deg: number): string {
  return `${Math.round(normalizeDeg(deg))}°`;
}

/**
 * Cihaz yönelim açılarından (alpha/beta/gamma) pusula açısı hesaplar.
 *
 * W3C DeviceOrientation dönüşüm matrisi R = Rz(alpha)·Rx(beta)·Ry(gamma)
 * kullanılır; sonuç Dünya çerçevesine (x: doğu, y: kuzey, z: yukarı)
 * göredir. Telefon yatay tutulduğunda ekranın üst kenarının, dik
 * tutulduğunda ise arka kameranın baktığı yön verilir — böylece her iki
 * tutuşta da tekillik oluşmaz.
 */
export function compassHeadingFromEuler(
  alpha: number,
  beta: number,
  gamma: number,
): number {
  const a = toRad(alpha);
  const b = toRad(beta);
  const g = toRad(gamma);

  const cA = Math.cos(a);
  const sA = Math.sin(a);
  const cB = Math.cos(b);
  const sB = Math.sin(b);
  const cG = Math.cos(g);
  const sG = Math.sin(g);

  // Ekran normalinin (cihaz +Z) düşey bileşeni: 1'e yakınsa telefon yatık.
  const screenNormalUp = cB * cG;

  let east: number;
  let north: number;

  if (Math.abs(screenNormalUp) > Math.SQRT1_2) {
    // Yatık tutuş: cihazın +Y ekseni (ekranın üst kenarı).
    east = -sA * cB;
    north = cA * cB;
  } else {
    // Dik tutuş: cihazın -Z ekseni (arka yüzün baktığı yön).
    east = -(cA * sG + sA * sB * cG);
    north = -(sA * sG - cA * sB * cG);
  }

  if (east === 0 && north === 0) return 0;
  return normalizeDeg(toDeg(Math.atan2(east, north)));
}

/**
 * Açılar için dairesel düşük geçiren filtre. Sıfır/360 sınırında sıçrama
 * yapmaması için vektör ortalaması kullanır.
 */
export function smoothAngle(previous: number, next: number, factor: number): number {
  if (!Number.isFinite(previous)) return next;
  return normalizeDeg(previous + angleDelta(previous, next) * factor);
}

/**
 * CSS dönüşü için sürekli (sarmayan) açı üretir: 359° → 1° geçişinde
 * ibrenin geriye doğru tam tur atmasını engeller.
 */
export function unwrapAngle(currentContinuous: number, targetDeg: number): number {
  return currentContinuous + angleDelta(normalizeDeg(currentContinuous), targetDeg);
}
